import type { ApolloError, ObservableQuery } from '@apollo/client';
import { Query, Mutation } from '@apollo/client/react/components';
import type { MutationFunction } from '@apollo/client/react/types/types';
import React from 'react';
import { memoize } from 'lodash';
import update from 'immutability-helper';

import Button from 'components/ButtonDeprecated';
import CenteredContainer from 'components/CenteredContainer';
import ConfirmDialog from 'components/ConfirmDialog';
import CSVUploadLoadingState from 'components/File/CSVUploadLoadingState';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import StudentIdListTable from 'chat/components/StudentIdListTable';
import TargetByStudentIdSelectPageRole from './TargetByStudentIdSelectPageRole';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import PencilImage from 'assets/images/pencil.svg';
import HeartImage from 'assets/images/heart.svg';

import GET_STUDENT_ID_LIST_QUERY from '../queries/GetStudentIdListQuery.gql';
import type {
  GetStudentIdList,
  GetStudentIdListVariables,
} from '../queries/__generated__/GetStudentIdList';

import PUT_STUDENT_ID_LIST_MUTATION from '../queries/PutStudentIdListMutation.gql';
import type {
  putStudentIdList,
  putStudentIdListVariables,
} from '../queries/__generated__/putStudentIdList';

export type Props = {
  studentIdListUuid: string;
  csvName: string;
  orgUuid: string;
  onContinue: (studentIdList: any, csvName: string) => void;
  onMount: () => void;
  onBack: () => void;
};

type InnerProps = {
  mutate: MutationFunction<putStudentIdList, putStudentIdListVariables>;
  data?: GetStudentIdList;
  loading?: boolean;
  error?: ApolloError;
  fetchMore: ObservableQuery<GetStudentIdList, GetStudentIdListVariables>['fetchMore'];
  refetch: ObservableQuery<GetStudentIdList, GetStudentIdListVariables>['refetch'];
};

const prefixEvent = (suffix: string) =>
  `announcements.target_by_student_id.list_of_uploaded_users.${suffix}`;
function trackEvent(key: string, properties: any = {}) {
  EventTracking.trackKey(prefixEvent(key), properties);
}

export class TargetByStudentIdSelectPage extends React.Component<Props & InnerProps> {
  componentDidMount() {
    this.props.onMount();
    trackEvent('view');
  }

  toggleRoleInclusion = async (
    roleToToggle: 'parent' | 'student',
    studentIdListUuid: string,
    fileUuid: string,
    organizationUuid: string,
    includeParents: boolean,
    includeStudents: boolean
  ) => {
    if (roleToToggle === 'parent') {
      const event = includeParents ? 'parent_checkbox.select' : 'parent_checkbox.unselect';
      trackEvent(event);
    }

    if (roleToToggle === 'student') {
      const event = includeStudents ? 'student_checkbox.select' : 'student_checkbox.unselect';
      trackEvent(event);
    }

    if (!includeParents && !includeStudents) {
      ToastActions.showErrorToast('Please select at least one set of recipients for this message.');
      return;
    }

    const handleError = (error: Error, consoleError?: string) => {
      ToastActions.showErrorToast(error.message);
      console.warn(error);
      if (consoleError) {
        console.error(consoleError);
      }
    };
    try {
      const response = await this.props.mutate({
        variables: {
          input: {
            uuid: studentIdListUuid,
            fileUuid,
            organizationUuid,
            includeParents,
            includeStudents,
          },
        },
        optimisticResponse: {
          putStudentIdList: {
            __typename: 'PutStudentIdListPayload',
            results: {
              __typename: 'StudentIdList',
              uuid: studentIdListUuid,
              fileUuid,
              includeParents,
              includeStudents,
            },
            error: null,
          },
        },
      });

      const error = response?.data?.putStudentIdList?.error;
      if (error) {
        handleError(error as any);
      }
    } catch (error) {
      handleError(
        error as any,
        `TBSI - putStudentIdList runtime error while attempting to toggle ${roleToToggle} inclusion`
      );
    }
  };

  loadMoreStudents = memoize((cursor: string) => {
    this.props.fetchMore({
      variables: {
        uuid: this.props.studentIdListUuid,
        cursor,
        limit: 20,
      },
    });
  });

  render() {
    const { data, loading, csvName } = this.props;

    if (loading && !data?.studentIdList) {
      return (
        <CenteredContainer>
          <CSVUploadLoadingState
            text={'Loading student and parent data...'}
            longListWarning={true}
          />
        </CenteredContainer>
      );
    }

    if (!data?.studentIdList) {
      ToastActions.showErrorToast(
        'Looks like something went wrong - please email support@remindhq.com for further assistance.'
      );
      this.props.onBack();
      return (
        <ErrorReporter error="TBSI - tried to render TargetByStudentIdSelectPage without a student ID list" />
      );
    }

    const {
      uuid,
      fileUuid,
      organizationUuid,
      includeStudents,
      includeParents,
      parentsCount,
      studentsCount,
      studentsWithoutParentsCount,
    } = data.studentIdList;

    const totalCount = (includeParents ? parentsCount : 0) + (includeStudents ? studentsCount : 0);

    return (
      <React.Fragment>
        <div className="TargetByStudentIdSelectPage-main">
          <StudentIdListTable
            studentIdList={data.studentIdList}
            loading={!!loading}
            orgUuid={this.props.orgUuid}
            onLoadMoreStudents={this.loadMoreStudents}
            onParentAdd={() => {
              this.loadMoreStudents.cache?.clear?.();
              this.props.refetch();
            }}
            eventKeys={{
              unidentifiedStudentsHelpCTA: prefixEvent('unidentified_students.help_cta.click'),
              unidentifiedStudentsExpand: prefixEvent('unidentified_students.expand'),
              unidentifiedStudentsCollapse: prefixEvent('unidentified_students.collapse'),
            }}
          />
        </div>
        <div className="TargetByStudentIdSelectPage-sidebar">
          <MediaObject figure="right" center className="TargetByStudentIdSelectPage-sidebar-header">
            <Heading level="4">Selected</Heading>
            <Heading level="4">{totalCount}</Heading>
          </MediaObject>
          <TargetByStudentIdSelectPageRole
            onClick={() => {
              this.toggleRoleInclusion(
                'student',
                uuid,
                fileUuid,
                organizationUuid,
                includeParents,
                !includeStudents
              );
            }}
            icon={PencilImage}
            roleType="student"
            personCount={studentsCount}
            checked={includeStudents}
            enabled
          />
          <TargetByStudentIdSelectPageRole
            onClick={() => {
              this.toggleRoleInclusion(
                'parent',
                uuid,
                fileUuid,
                organizationUuid,
                !includeParents,
                includeStudents
              );
            }}
            icon={HeartImage}
            roleType="parent"
            personCount={parentsCount}
            checked={includeParents}
            enabled={!!parentsCount}
            orphanCount={studentsWithoutParentsCount}
          />
        </div>
        <div className="TargetByStudentIdSelectPage-footer">
          <ModalTrigger
            modal={
              <ConfirmDialog
                title="Are you sure you want to go back?"
                onConfirm={this.props.onBack}
                confirmEventKey={prefixEvent('go_back_dialog.go_back.click')}
                cancelEventKey={prefixEvent('go_back_dialog.cancel.click')}
              >
                <Text color="graphite">Your uploaded list of student numbers will be cleared.</Text>
              </ConfirmDialog>
            }
          >
            <Button level="secondary" data-test="back-button" eventKey={prefixEvent('back.click')}>
              Back
            </Button>
          </ModalTrigger>
          <Button
            onClick={() => this.props.onContinue(data.studentIdList, csvName)}
            disabled={!includeParents && !includeStudents}
            data-test="continue-button"
            eventKey={prefixEvent('continue.click')}
          >
            Continue
          </Button>
        </div>
      </React.Fragment>
    );
  }
}

export default function TargetByStudentIdSelectPageContainer(props: Props) {
  return (
    <Mutation<putStudentIdList, putStudentIdListVariables> mutation={PUT_STUDENT_ID_LIST_MUTATION}>
      {(mutate) => (
        <Query<GetStudentIdList, GetStudentIdListVariables>
          query={GET_STUDENT_ID_LIST_QUERY}
          variables={{
            uuid: props.studentIdListUuid,
            limit: 20,
          }}
          notifyOnNetworkStatusChange
        >
          {({ data, loading, error, fetchMore, refetch }) => (
            <TargetByStudentIdSelectPage
              {...props}
              {...{ mutate, data, loading, error, fetchMore, refetch }}
            />
          )}
        </Query>
      )}
    </Mutation>
  );
}
